import { ReactComponent as ChevronLeftIcon } from "feather-icons/dist/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "feather-icons/dist/icons/chevron-right.svg";
import { ReactComponent as BookmarkIcon } from "feather-icons/dist/icons/bookmark.svg";
import { ReactComponent as ListIcon } from "feather-icons/dist/icons/list.svg";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import tw from 'twin.macro';
import styled from "styled-components";
import ReactModalAdapter from "../helpers/ReactModalAdapter.js";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import logo from "images/lra_pink.svg";
import Contents from "./ContentsDialog.js";
import ReactAudioPlayer from "react-audio-player";
import { api } from "supabase/database/api.ts";


const ControlButton = styled(PrimaryButtonBase)`
  ${tw`p-3 sm:p-2`}
  border-radius: 9999px !important;
  svg {
    ${tw`w-6 h-6`}
  }
  min-width: 44px;
  min-height: 44px;
`;
const PrevButton = tw(ControlButton)``;
const NextButton = tw(ControlButton)``;
const TableOfContentsButton = tw(ControlButton)``;
const BookmarkButton = styled(ControlButton)`
  svg { fill: ${({ $active }) => ($active ? "currentColor" : "none")}; }
`;
const ViewBookmarksButton = tw(ControlButton)``;
const NavPaginationContainer = tw.div`flex justify-center items-center gap-6 py-2`;
const ToolbarContainer = tw.div`flex flex-wrap justify-center items-center gap-3 w-full py-3`;
const PDFContainer = tw.div`flex justify-center mx-auto my-auto h-full w-full max-w-full max-h-full shadow-2xl`;
const Input = styled.input`
  ${tw`px-4 rounded-full font-medium bg-gray-100 border border-primary-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white text-center`}
  width: 5rem;
  min-height: 44px;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  appearance: textfield;
`;
const BookmarkBtnWrapper = tw.div`relative inline-flex`;
const CountBadge = styled.span`
  ${tw`absolute bg-white text-primary-500 border border-primary-200 text-xs font-bold rounded-full flex items-center justify-center`}
  top: -4px; right: -4px; width: 16px; height: 16px; line-height: 1;
`;

const BookmarksModal = styled(ReactModalAdapter)`
  &__overlay {
    ${tw`fixed inset-0 z-50 bg-black bg-opacity-75`}
  }
  &__content {
    ${tw`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 shadow-2xl outline-none`}
    width: min(380px, 90vw);
    max-height: 75vh;
    overflow-y: auto;
  }
`;
const CloseModalButton = tw.button`absolute right-auto text-gray-400 hover:text-primary-500 transition-colors`;
const LogoImage = tw.img`h-10 mx-auto mb-3`;
const ModalHeading = tw.h2`text-xl font-extrabold text-center mb-4`;
const BookmarkItem = tw.div`w-full flex items-center justify-between px-4 py-2 rounded-lg hover:bg-gray-100 text-sm text-gray-700 transition-colors cursor-pointer`;
const DeleteButton = tw.button`ml-2 text-gray-300 hover:text-red-500 transition-colors`;
const NameModal = styled(ReactModalAdapter)`
  &__overlay {
    ${tw`fixed inset-0 z-50 bg-black bg-opacity-75`}
  }
  &__content {
    ${tw`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 shadow-2xl outline-none`}
    width: min(340px, 90vw);
  }
`;
const ModalLabel = tw.p`text-sm text-gray-500 mb-2`;
const LabelInput = styled.input`
  ${tw`w-full px-4 py-3 rounded-lg bg-gray-100 border border-primary-200 placeholder-gray-400 text-sm focus:outline-none focus:border-primary-400 focus:bg-white mb-4`}
`;
const ModalActions = tw.div`flex gap-2`;
const ConfirmButton = styled(PrimaryButtonBase)`${tw`flex-1 rounded-lg py-2 text-sm`}`;
const CancelButton = tw.button`flex-1 py-2 text-sm rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100`;
const PageBadge = tw.span`ml-2 text-xs bg-gray-100 text-primary-700 px-2 py-1 rounded-full`;

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PdfViewer({ pdf, audio, table, contentId, contentTitle }) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(() => {
    if (table && contentId) {
      const saved = parseInt(localStorage.getItem(`lra_pdf_${table}_${contentId}`), 10);
      if (saved >= 1) return saved;
    }
    return 1;
  });
  const [inputValue, setInputValue] = useState(pageNumber);
  const [loading, setLoading] = useState(true);
  const [pageWidth, setPageWidth] = useState(0);
  const [message, setMessage] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [content, setContent] = useState([]);
  const [renderPage, setRenderPage] = useState(false);
  const [pdfBookmarks, setPdfBookmarks] = useState([]);
  const [bookmarkModalIsOpen, setBookmarkModalIsOpen] = useState(false);
  const [namingBookmark, setNamingBookmark] = useState(false);
  const [bookmarkLabel, setBookmarkLabel] = useState("");
  const [bookmarkBusy, setBookmarkBusy] = useState(false);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    setContent(pdf.contents);

    const pageParam = parseInt(searchParams.get("page"), 10);
    if (pageParam >= 1) setPageNumber(pageParam);

    if (table && contentId) {
      api.bookmarkQueries.getPdfBookmarks({ table_name: table, content_id: contentId })
        .then(data => setPdfBookmarks(data || []))
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    setInputValue(pageNumber);
    if (table && contentId) localStorage.setItem(`lra_pdf_${table}_${contentId}`, pageNumber);
  }, [pageNumber]);

  const toggleModal = () => setModalIsOpen(!modalIsOpen);
  const toggleDialog = () => {
    setContent(pdf.contents);
    setDialogIsOpen(true);
  };

  function onDocumentLoadSuccess({ numPages: nextNumPages }) {
    setNumPages(nextNumPages);
    setRenderPage(true);
  }

  function onPageLoadSuccess() {
    setPageWidth(window.innerWidth);
    setLoading(false);
  }

  const handlePdfLoadError = (error) => {
    console.error('PDF load error:', error);
  }

  const options = useMemo(() => {
    return {
      cMapUrl: "cmaps/",
      cMapPacked: true,
      standardFontDataUrl: "standard_fonts/"
    }
  }, []);

  function goToNextPage() {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  }

  function goToPreviousPage() {
    setPageNumber((prevPageNumber) => prevPageNumber - 1);
  }

  function goToPage(newPage) {
    if (newPage >= 1 && newPage <= numPages) {
      setPageNumber(newPage);
    }
  }

  const currentBookmark = pdfBookmarks.find(b => b.page_number === pageNumber);
  const isBookmarked = Boolean(currentBookmark);

  async function toggleBookmark() {
    if (!table || !contentId || bookmarkBusy) return;
    if (isBookmarked) {
      setBookmarkBusy(true);
      try {
        await api.bookmarkMutations.removeBookmark({ id: currentBookmark.id });
        setPdfBookmarks(prev => prev.filter(b => b.id !== currentBookmark.id));
      } finally {
        setBookmarkBusy(false);
      }
    } else {
      setBookmarkLabel("");
      setNamingBookmark(true);
    }
  }

  async function confirmBookmark() {
    if (bookmarkBusy) return;
    setBookmarkBusy(true);
    try {
      const result = await api.bookmarkMutations.addBookmark({
        table_name: table,
        content_id: contentId,
        content_title: contentTitle,
        page_number: pageNumber,
        label: bookmarkLabel.trim() || undefined,
        content_path: window.location.pathname,
      });
      setPdfBookmarks(prev => [...prev, result].sort((a, b) => a.page_number - b.page_number));
      setNamingBookmark(false);
    } finally {
      setBookmarkBusy(false);
    }
  }

  async function deleteBookmark(id) {
    if (bookmarkBusy) return;
    setBookmarkBusy(true);
    try {
      await api.bookmarkMutations.removeBookmark({ id });
      setPdfBookmarks(prev => prev.filter(b => b.id !== id));
    } finally {
      setBookmarkBusy(false);
    }
  }

  return (
    <>
      <div hidden={loading} tw="relative">
        <ToolbarContainer>
          { audio === true
            ? pdf.contents != null && (
                <ReactAudioPlayer
                  src={`https://zegleedmcpswaxynxnta.supabase.co/storage/v1/object/public/assets/${pdf.contents}.mp3`}
                  controls
                  controlsList="nodownload"
                />
              )
            : <>
                { Array.isArray(pdf.contents) && pdf.contents.length > 0 && <>
                  <Contents
                    open={dialogIsOpen}
                    handleClose={() => setDialogIsOpen(false)}
                    contents={pdf.contents}
                    onSelect={(page) => setPageNumber(page)}
                    render={!audio}
                  />
                  <TableOfContentsButton onClick={toggleDialog}>
                    Contents
                  </TableOfContentsButton>
                </> }

                <Input
                  type="number"
                  value={inputValue}
                  min={1}
                  max={numPages}
                  onChange={e => setInputValue(e.target.value)}
                  onBlur={e => goToPage(parseInt(e.target.value, 10))}
                  onKeyDown={e => e.key === "Enter" && goToPage(parseInt(e.target.value, 10))}
                />

                { table && contentId && <>
                  <BookmarkButton onClick={toggleBookmark} disabled={bookmarkBusy} $active={isBookmarked} title={isBookmarked ? "Remove bookmark" : "Bookmark this page"}>
                    <BookmarkIcon tw="h-6 w-6" aria-hidden="true" />
                  </BookmarkButton>
                  { pdfBookmarks.length > 0 &&
                    <BookmarkBtnWrapper>
                      <ViewBookmarksButton onClick={() => setBookmarkModalIsOpen(true)} title="View bookmarks">
                        <ListIcon tw="h-6 w-6" aria-hidden="true" />
                      </ViewBookmarksButton>
                      <CountBadge>{pdfBookmarks.length}</CountBadge>
                    </BookmarkBtnWrapper>
                  }
                </> }
              </>
          }
        </ToolbarContainer>

        <br></br>
        <PDFContainer>
          <Document
            file={pdf.file}
            onLoadSuccess={onDocumentLoadSuccess}
            options={options}
            renderMode="canvas"
            onLoadError={handlePdfLoadError}
          >
            { renderPage ?
              <>
                  <Page
                    tw=""
                    key={pageNumber}
                    pageNumber={pageNumber}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    onLoadSuccess={onPageLoadSuccess}
                    onRenderError={() => setLoading(false)}
                    width={Math.max(pageWidth * 0.6, 340)}
                  />
              </>
              : ""
            }
          </Document>
        </PDFContainer>

        <br></br>

        <NavPaginationContainer>
          <PrevButton onClick={goToPreviousPage} disabled={pageNumber <= 1}>
            <ChevronLeftIcon tw="h-10 w-10" aria-hidden="true" />
          </PrevButton>
          <span> {pageNumber} / {numPages}</span>
          <NextButton onClick={goToNextPage} disabled={pageNumber >= numPages}>
            <ChevronRightIcon tw="h-10 w-10" aria-hidden="true" />
          </NextButton>
        </NavPaginationContainer>
      </div>

      <BookmarksModal
        isOpen={bookmarkModalIsOpen}
        onRequestClose={() => setBookmarkModalIsOpen(false)}
        shouldCloseOnOverlayClick={true}
      >
        <CloseModalButton onClick={() => setBookmarkModalIsOpen(false)}>
          <CloseIcon tw="w-5 h-5" />
        </CloseModalButton>
        <LogoImage src={logo} />
        <ModalHeading>Bookmarks</ModalHeading>
        {pdfBookmarks.map((b) => (
          <BookmarkItem key={b.id} onClick={() => { goToPage(b.page_number); setBookmarkModalIsOpen(false); }}>
            <span>{b.label || `Page ${b.page_number}`}{b.label && <PageBadge tw="ml-2 bg-primary-100 text-primary-400">p.{b.page_number}</PageBadge>}</span>
            <DeleteButton onClick={(e) => { e.stopPropagation(); deleteBookmark(b.id); }} disabled={bookmarkBusy} title="Remove">
              <CloseIcon tw="w-4 h-4" />
            </DeleteButton>
          </BookmarkItem>
        ))}
      </BookmarksModal>

      <NameModal
        isOpen={namingBookmark}
        onRequestClose={() => setNamingBookmark(false)}
        shouldCloseOnOverlayClick={true}
      >
        <LogoImage src={logo} />
        <ModalHeading>Bookmark Page {pageNumber}</ModalHeading>
        <ModalLabel>Give this bookmark a name (optional)</ModalLabel>
        <LabelInput
          autoFocus
          placeholder={`Page ${pageNumber}`}
          value={bookmarkLabel}
          onChange={e => setBookmarkLabel(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") confirmBookmark(); if (e.key === "Escape") setNamingBookmark(false); }}
        />
        <ModalActions>
          <CancelButton onClick={() => setNamingBookmark(false)} disabled={bookmarkBusy}>Cancel</CancelButton>
          <ConfirmButton onClick={confirmBookmark} disabled={bookmarkBusy}>{bookmarkBusy ? "Saving..." : "Save Bookmark"}</ConfirmButton>
        </ModalActions>
      </NameModal>
    </>
  );
}
